import { collection, getDocs, doc, getDoc, setDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { sendSlackNotification } from './slackNotification';

/**
 * 알림 시스템 유틸리티
 *
 * 재고 관련 알림을 감지하고 생성하는 핵심 로직
 */

/**
 * 알림 설정을 불러옵니다
 */
export const getAlertSettings = async () => {
  try {
    const settingsRef = doc(db, 'settings', 'alerts');
    const docSnap = await getDoc(settingsRef);

    if (docSnap.exists()) {
      return docSnap.data();
    }

    // 기본 설정값
    return {
      lowStockThreshold: 5,
      longTermUnsoldDays: 90,
      reservationExpiryWarning: 3,
      consignmentSettlementDays: 60,
      enableDashboardAlerts: true,
      enableSlackAlerts: true,
      enableEmailAlerts: false,
    };
  } catch (error) {
    console.error('알림 설정 로드 오류:', error);
    return null;
  }
};

/**
 * 저재고 알림 체크
 */
export const checkLowStockAlerts = async (settings) => {
  const alerts = [];

  try {
    const inventoryRef = collection(db, 'inventory');
    const snapshot = await getDocs(inventoryRef);

    snapshot.docs.forEach(doc => {
      const item = { id: doc.id, ...doc.data() };

      // 판매 가능 상태이고 재고 수량이 임계값 미만인 경우
      if (item.status === '판매가능' && item.quantity < settings.lowStockThreshold) {
        alerts.push({
          type: 'low_stock',
          severity: 'warning',
          itemId: item.id,
          itemName: item.productName,
          message: `저재고 알림: ${item.productName} (현재 수량: ${item.quantity})`,
          data: {
            currentQuantity: item.quantity,
            threshold: settings.lowStockThreshold,
            location: item.location,
            category: item.category
          },
          createdAt: new Date().toISOString()
        });
      }
    });
  } catch (error) {
    console.error('저재고 알림 체크 오류:', error);
  }

  return alerts;
};

/**
 * 장기 미판매 재고 알림 체크
 */
export const checkLongTermUnsoldAlerts = async (settings) => {
  const alerts = [];
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - settings.longTermUnsoldDays);

  try {
    const inventoryRef = collection(db, 'inventory');
    const snapshot = await getDocs(inventoryRef);

    snapshot.docs.forEach(doc => {
      const item = { id: doc.id, ...doc.data() };

      // 판매 가능 상태인 제품만 체크
      if (item.status === '판매가능') {
        const registeredDate = item.createdAt?.toDate?.() || new Date(item.createdAt);

        // 등록일이 cutoffDate보다 이전인 경우
        if (registeredDate < cutoffDate) {
          const daysUnsold = Math.floor((new Date() - registeredDate) / (1000 * 60 * 60 * 24));

          alerts.push({
            type: 'long_term_unsold',
            severity: 'info',
            itemId: item.id,
            itemName: item.productName,
            message: `장기 미판매: ${item.productName} (${daysUnsold}일)`,
            data: {
              daysUnsold,
              registeredDate: registeredDate.toISOString(),
              location: item.location,
              category: item.category,
              salePrice: item.salePrice
            },
            createdAt: new Date().toISOString()
          });
        }
      }
    });
  } catch (error) {
    console.error('장기 미판매 알림 체크 오류:', error);
  }

  return alerts;
};

/**
 * 예약 만료 알림 체크
 */
export const checkReservationExpiryAlerts = async (settings) => {
  const alerts = [];
  const warningDate = new Date();
  warningDate.setDate(warningDate.getDate() + settings.reservationExpiryWarning);

  try {
    const inventoryRef = collection(db, 'inventory');
    const snapshot = await getDocs(inventoryRef);

    snapshot.docs.forEach(doc => {
      const item = { id: doc.id, ...doc.data() };

      // 예약 상태이고 예약 만료일이 있는 경우
      if (item.status === '예약' && item.reservationExpiry) {
        const expiryDate = item.reservationExpiry?.toDate?.() || new Date(item.reservationExpiry);

        // 만료일이 경고 기간 내에 있는 경우
        if (expiryDate <= warningDate && expiryDate >= new Date()) {
          const daysRemaining = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));

          alerts.push({
            type: 'reservation_expiry',
            severity: 'warning',
            itemId: item.id,
            itemName: item.productName,
            message: `예약 만료 예정: ${item.productName} (${daysRemaining}일 남음)`,
            data: {
              daysRemaining,
              expiryDate: expiryDate.toISOString(),
              customerName: item.reservationCustomer || '미지정',
              customerPhone: item.reservationPhone || '',
              location: item.location
            },
            createdAt: new Date().toISOString()
          });
        }
      }
    });
  } catch (error) {
    console.error('예약 만료 알림 체크 오류:', error);
  }

  return alerts;
};

/**
 * 위탁 정산 기한 알림 체크
 */
export const checkConsignmentSettlementAlerts = async (settings) => {
  const alerts = [];
  const settlementDueDate = new Date();
  settlementDueDate.setDate(settlementDueDate.getDate() - settings.consignmentSettlementDays);

  try {
    const inventoryRef = collection(db, 'inventory');
    const q = query(inventoryRef, where('category', '==', '위탁'));
    const snapshot = await getDocs(q);

    snapshot.docs.forEach(doc => {
      const item = { id: doc.id, ...doc.data() };

      // 위탁 제품이고 판매 완료된 경우
      if (item.status === '판매완료' && item.saleDate) {
        const saleDate = item.saleDate?.toDate?.() || new Date(item.saleDate);

        // 판매일이 정산 기한을 넘긴 경우
        if (saleDate <= settlementDueDate) {
          const daysSinceSale = Math.floor((new Date() - saleDate) / (1000 * 60 * 60 * 24));

          alerts.push({
            type: 'consignment_settlement',
            severity: 'high',
            itemId: item.id,
            itemName: item.productName,
            message: `위탁 정산 필요: ${item.productName} (판매 후 ${daysSinceSale}일)`,
            data: {
              daysSinceSale,
              saleDate: saleDate.toISOString(),
              consignor: item.consignor || '미지정',
              salePrice: item.salePrice,
              commissionRate: item.commissionRate || 0
            },
            createdAt: new Date().toISOString()
          });
        }
      }
    });
  } catch (error) {
    console.error('위탁 정산 알림 체크 오류:', error);
  }

  return alerts;
};

/**
 * 모든 알림을 체크하고 생성합니다
 */
export const generateAlerts = async () => {
  try {
    const settings = await getAlertSettings();

    if (!settings) {
      console.error('알림 설정을 불러올 수 없습니다');
      return [];
    }

    // 모든 유형의 알림 체크
    const [lowStockAlerts, unsoldAlerts, reservationAlerts, settlementAlerts] = await Promise.all([
      checkLowStockAlerts(settings),
      checkLongTermUnsoldAlerts(settings),
      checkReservationExpiryAlerts(settings),
      checkConsignmentSettlementAlerts(settings)
    ]);

    // 모든 알림 통합
    const allAlerts = [
      ...lowStockAlerts,
      ...unsoldAlerts,
      ...reservationAlerts,
      ...settlementAlerts
    ];

    // Firestore에 알림 저장
    if (allAlerts.length > 0) {
      await saveAlerts(allAlerts, settings);
    }

    return allAlerts;
  } catch (error) {
    console.error('알림 생성 오류:', error);
    return [];
  }
};

/**
 * 알림을 Firestore에 저장합니다
 */
export const saveAlerts = async (alerts, settings) => {
  try {
    const batch = [];
    const alertsRef = collection(db, 'alerts');

    // 각 알림을 Firestore에 저장
    for (const alert of alerts) {
      const alertDoc = doc(alertsRef);
      batch.push(
        setDoc(alertDoc, {
          ...alert,
          read: false,
          dismissed: false,
          createdAt: serverTimestamp()
        })
      );
    }

    // 모든 저장 작업 실행
    await Promise.all(batch);

    // Slack 알림이 활성화된 경우
    if (settings.enableSlackAlerts) {
      await sendAlertsToSlack(alerts);
    }

    console.log(`${alerts.length}개의 알림이 생성되었습니다`);
  } catch (error) {
    console.error('알림 저장 오류:', error);
  }
};

/**
 * Slack으로 알림을 전송합니다
 */
export const sendAlertsToSlack = async (alerts) => {
  try {
    // 심각도별로 그룹화
    const highSeverity = alerts.filter(a => a.severity === 'high');
    const warningSeverity = alerts.filter(a => a.severity === 'warning');
    const infoSeverity = alerts.filter(a => a.severity === 'info');

    let message = '📊 *재고 관리 시스템 알림*\n\n';

    if (highSeverity.length > 0) {
      message += `🚨 *중요 알림* (${highSeverity.length}건)\n`;
      highSeverity.forEach(alert => {
        message += `• ${alert.message}\n`;
      });
      message += '\n';
    }

    if (warningSeverity.length > 0) {
      message += `⚠️ *경고 알림* (${warningSeverity.length}건)\n`;
      warningSeverity.forEach(alert => {
        message += `• ${alert.message}\n`;
      });
      message += '\n';
    }

    if (infoSeverity.length > 0) {
      message += `ℹ️ *정보 알림* (${infoSeverity.length}건)\n`;
      infoSeverity.forEach(alert => {
        message += `• ${alert.message}\n`;
      });
    }

    await sendSlackNotification(message);
  } catch (error) {
    console.error('Slack 알림 전송 오류:', error);
  }
};

/**
 * 알림을 읽음으로 표시합니다
 */
export const markAlertAsRead = async (alertId) => {
  try {
    const alertRef = doc(db, 'alerts', alertId);
    await setDoc(alertRef, { read: true }, { merge: true });
  } catch (error) {
    console.error('알림 읽음 처리 오류:', error);
  }
};

/**
 * 알림을 무시(삭제)합니다
 */
export const dismissAlert = async (alertId) => {
  try {
    const alertRef = doc(db, 'alerts', alertId);
    await setDoc(alertRef, { dismissed: true }, { merge: true });
  } catch (error) {
    console.error('알림 무시 처리 오류:', error);
  }
};

/**
 * 읽지 않은 알림을 불러옵니다
 */
export const getUnreadAlerts = async () => {
  try {
    const alertsRef = collection(db, 'alerts');
    const q = query(
      alertsRef,
      where('read', '==', false),
      where('dismissed', '==', false),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('알림 불러오기 오류:', error);
    return [];
  }
};

/**
 * 모든 활성 알림을 불러옵니다 (읽음/안읽음 모두)
 */
export const getAllActiveAlerts = async () => {
  try {
    const alertsRef = collection(db, 'alerts');
    const q = query(
      alertsRef,
      where('dismissed', '==', false),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('알림 불러오기 오류:', error);
    return [];
  }
};
